// packages/api/src/PublicPartnerService.ts
//
// PUBLIC sponsorship application client (#A4.5) — the anonymous write behind the partner-packages
// block's CTA. Mirrors PublicContactService: runs through the injected client so it works in the
// BROWSER, because the apply form is an interactive client component.
//
// ⚠ Notice what the input does NOT carry: no amount, no currency, no partner kind, no event. The
// server reads all of those from the offering. A field the client could send is a field an
// applicant controls, and one of them decides what a sponsorship costs.
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";
import { ANSWER_IMAGE_FIELDS, QUESTION_IMAGE_FIELDS, executeMutationImageSafe, executeQueryImageSafe, imageSafeDocs } from "./answerImageFields";
import type { PublicRsvpAnswerInput, PublicRsvpFormQuestion, PublicRsvpMineAnswer } from "./PublicRsvpService";
import { refusedNewInputs } from "./refusedNewInputs";

export interface PublicPartnerApplyInput {
  /** The offering being applied for. Everything about the deal is read from it. */
  eventPartnerPackageId: string;
  /** Who is applying. Required — a sponsorship cannot be anonymous. */
  orgName: string;
  /** ⚠ Split, not one field — approval creates a directory person and must not guess the split. */
  firstName?: string | null;
  lastName?: string | null;
  /** Required: the organizer has to be able to reply. */
  email: string;
  phone?: string | null;
  websiteUrl?: string | null;
  message?: string | null;
  /** The applicant's browser session, so they can find their application again. */
  visitorId?: string | null;
  /**
   * Answers to the host's custom questions for this partner kind.
   * ⚠ The SAME input shape the RSVP form and ticket checkout submit — one question engine, one
   * shape. A partner-specific shape would drift on the details that decide whether an answer saves.
   */
  answers?: PublicRsvpAnswerInput[] | null;
}

/** The apply form for one offering: what the host wants to know before they will consider you. */
export interface PublicPartnerApplyForm {
  eventPartnerPackageId?: string | null;
  /** The kind being applied for — it decides which questions are asked. */
  partnerKind?: string | null;
  isOpenForApplications?: boolean | null;
  /** In the host's order. A question with no kinds is asked of every kind. */
  questions?: PublicRsvpFormQuestion[] | null;
}

export interface PublicPartnerApplication {
  id?: string;
  applicationStatus?: string | null;
  /** What they will be charged, frozen at the moment of applying. */
  amount?: number | null;
  currencyCode?: string | null;
  paymentStatus?: string | null;
  /** True when this offering charges at application time — the client must now confirm payment. */
  paymentRequired?: boolean | null;
  /** Present ONLY when paymentRequired. */
  clientSecret?: string | null;
  publishableKey?: string | null;
  /** Stripe.js MUST be initialised with this for a direct charge on the host's account. */
  connectedAccountId?: string | null;
}

const APPLY_FORM_QUERY = imageSafeDocs(`
  query PublicPartnerApplyForm($params: PublicPartnerApplyFormInput) {
    publicPartnerApplyForm(params: $params) {
      code
      data {
        eventPartnerPackageId
        partnerKind
        isOpenForApplications
        questions {
          id questionType valueType title helpText placeholder
          isRequired requiredScope personField ${QUESTION_IMAGE_FIELDS} askOncePerGroup
          conditionMatch orderNo
          options { id label value price }
        }
      }
      errors { name errors warnings }
    }
  }
`);

const CONFIRM_MUTATION = gql`
  mutation PublicPartnerConfirm($params: PublicPartnerConfirmInput) {
    publicPartnerConfirm(params: $params) {
      code
      data {
        id
        applicationStatus
        amount
        currencyCode
        paymentStatus
      }
      errors { name errors warnings }
    }
  }
`;

const APPLY_MUTATION = gql`
  mutation PublicPartnerApply($params: PublicPartnerApplyInput) {
    publicPartnerApply(params: $params) {
      code
      data {
        id
        applicationStatus
        amount
        currencyCode
        paymentStatus
        paymentRequired
        clientSecret
        publishableKey
        connectedAccountId
      }
      errors { name errors warnings }
    }
  }
`;

/**
 * An applicant's view of their OWN application — the "my application" surface.
 *
 * ⚠ AN ALLOWLIST ON THE SERVER, not a redaction. The row it comes from also carries the host's
 * review notes and the internal decision trail; those are not omitted here, they never leave the
 * API at all.
 */
export interface PublicPartnerMyApplication {
  id?: string | null;
  applicationStatus?: string | null;
  partnerKind?: string | null;
  /** The offering — used ONLY to load the same question set the form was built from. */
  eventPartnerPackageId?: string | null;
  /** The tier's name ("Gold Sponsor"), from the host's catalog. */
  packageName?: string | null;
  orgName?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
  websiteUrl?: string | null;
  message?: string | null;
  /** What the HOST is owed. */
  amount?: number | null;
  serviceFeeAmount?: number | null;
  /** ⚠ What the SPONSOR was charged. Quote this one. */
  totalAmount?: number | null;
  /** Our service fee, as far as the sponsor pays it. Null: the charge cannot be split. */
  serviceFeeToSponsor?: number | null;
  /** The card processing fee, as far as the sponsor pays it. */
  cardFeeToSponsor?: number | null;
  /** The non-refundable slice a decline or withdrawal keeps, if any. */
  applicationFeeAmount?: number | null;
  currencyCode?: string | null;
  paymentStatus?: string | null;
  submittedAt?: string | null;
  /** The answers already given, for prefilling an edit. */
  answers?: PublicRsvpMineAnswer[] | null;
  /**
   * ⚠ THE SERVER'S ANSWER, never re-derived in the browser. It is `applicationStatus === 'pending'`
   * today, but the server is what enforces the refusal — a second copy of the rule is how the two
   * start disagreeing.
   */
  canEdit?: boolean | null;
  /**
   * ⚠ Money is still owed AND can be paid now — an APPROVED application on an offering that charges
   * after approval. Decided by the SAME server function that refuses the charge, so a Pay button
   * never appears for something the server will not take money for.
   */
  canPay?: boolean | null;
}

/** What an applicant may change before a decision: DETAILS AND ANSWERS ONLY (ruling 10). */
export interface PublicPartnerUpdateMineInput {
  applicationId: string;
  /** Proof this browser is the one that applied. There is no other authorization on this call. */
  visitorId: string;
  orgName?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
  websiteUrl?: string | null;
  message?: string | null;
  answers?: PublicRsvpAnswerInput[] | null;
}

/**
 * The applicant-visible fields, selected identically everywhere so the three operations that return
 * one can never disagree about what a "my application" is.
 */
const MY_APPLICATION_FIELDS = `
  id
  applicationStatus
  partnerKind
  eventPartnerPackageId
  packageName
  orgName
  firstName
  lastName
  email
  phone
  websiteUrl
  message
  amount
  serviceFeeAmount
  totalAmount
  serviceFeeToSponsor
  cardFeeToSponsor
  applicationFeeAmount
  currencyCode
  paymentStatus
  submittedAt
  answers { questionId valueText valueNumber valueBoolean valueDate optionIds valueAddress { streetAddress extraAddress city stateProvince postalCode country } ${ANSWER_IMAGE_FIELDS} }
  canEdit
  canPay
`;

const MINE_QUERY = imageSafeDocs(`
  query PublicPartnerApplicationsMine($params: PublicPartnerMineInput) {
    publicPartnerApplicationsMine(params: $params) {
      code
      data { ${MY_APPLICATION_FIELDS} }
      errors { name errors warnings }
    }
  }
`);

const UPDATE_MINE_MUTATION = imageSafeDocs(`
  mutation PublicPartnerUpdateMine($params: PublicPartnerUpdateMineInput) {
    publicPartnerUpdateMine(params: $params) {
      code
      data { ${MY_APPLICATION_FIELDS} }
      errors { name errors warnings }
    }
  }
`);

const REQUEST_CODE_MUTATION = gql`
  mutation PublicPartnerRequestCode($params: PublicPartnerRequestCodeInput) {
    publicPartnerRequestCode(params: $params) {
      code
      data { sent }
      errors { name errors warnings }
    }
  }
`;

const REDEEM_CODE_MUTATION = imageSafeDocs(`
  mutation PublicPartnerRedeemCode($params: PublicPartnerRedeemCodeInput) {
    publicPartnerRedeemCode(params: $params) {
      code
      data { ${MY_APPLICATION_FIELDS} }
      errors { name errors warnings }
    }
  }
`);

const FORGET_MINE_MUTATION = imageSafeDocs(`
  mutation PublicPartnerForgetMine($params: PublicPartnerForgetMineInput) {
    publicPartnerForgetMine(params: $params) {
      code
      data { ${MY_APPLICATION_FIELDS} }
      errors { name errors warnings }
    }
  }
`);

const REDEEM_LINK_MUTATION = imageSafeDocs(`
  mutation PublicPartnerRedeemLink($params: PublicPartnerRedeemLinkInput) {
    publicPartnerRedeemLink(params: $params) {
      code
      data { ${MY_APPLICATION_FIELDS} }
      errors { name errors warnings }
    }
  }
`);

const START_PAYMENT_MUTATION = gql`
  mutation PublicPartnerStartPayment($params: PublicPartnerStartPaymentInput) {
    publicPartnerStartPayment(params: $params) {
      code
      data {
        id
        applicationStatus
        amount
        serviceFeeAmount
        totalAmount
        currencyCode
        paymentStatus
        paymentRequired
        clientSecret
        publishableKey
        connectedAccountId
      }
      errors { name errors warnings }
    }
  }
`;

export class PublicPartnerService {
  /**
   * Submit a sponsorship application.
   *
   * ⚠ The application is recorded even when payment is still owed, so an abandoned card entry
   * leaves the host a visible lead to chase rather than a silent gap. A `paymentRequired` result
   * carries the Stripe handoff — confirm it, and the webhook marks the application paid.
   */
  /**
   * The custom questions for one offering, scoped to its partner kind.
   *
   * ⚠ Its OWN query, not part of the page read — the packages block lists every tier and the
   * questions belong to the one an applicant actually opened. Mirrors `publicRsvpForm`.
   */
  /** `visitorId` (2026-10-04): on an invite-only event the form is its guests' only — sent only when there is one. */
  static async applyForm(eventPartnerPackageId: string, options: Record<string, any> = {}, visitorId: string | null = null): Promise<OpResult> {
    const vid = (visitorId ?? "").trim();

    const opts = { fetchPolicy: "network-only", ...options };
    const result = await executeQueryImageSafe(APPLY_FORM_QUERY, { params: { eventPartnerPackageId, ...(vid ? { visitorId: vid } : {}) } }, opts);

    // An API / gateway deployed after the site does not know `visitorId` here yet — read the form as before.
    return vid && refusedNewInputs(result, ["visitorId"]) ? executeQueryImageSafe(APPLY_FORM_QUERY, { params: { eventPartnerPackageId } }, opts) : result;
  }

  static async apply(params: PublicPartnerApplyInput): Promise<OpResult> {
    return ApiService.executeMutation(APPLY_MUTATION, { params });
  }

  /**
   * Tell the server the card cleared, so the application is marked paid and lands in the host's
   * money ledger straight away.
   *
   * ⚠ WITHOUT THIS CALL THE PAYMENT IS INVISIBLE. The drawer used to show its success screen and
   * say nothing to the server, leaving the application to be marked paid only if Stripe's webhook
   * arrived — so a sponsor who had genuinely paid still read as "Nothing taken yet" in the
   * organizer's inbox. This is the sponsorship twin of `PublicTicketService.finalize`.
   *
   * ⚠ It sends ONLY the application id. The server reads the PaymentIntent id from its own row and
   * verifies the charge with Stripe; this call is a nudge to go and look, never an assertion that
   * the money arrived.
   */
  static async confirm(applicationId: string, options: Record<string, any> = {}): Promise<OpResult> {
    return ApiService.executeMutation(CONFIRM_MUTATION, { params: { applicationId } }, options);
  }
  /**
   * The applications THIS BROWSER submitted for an event.
   *
   * ⚠ Keyed on the visitor cookie and NOTHING else — an application id in the request would let
   * anybody read one by guessing. An unknown browser gets an EMPTY LIST, never an error: a sponsor
   * on a new device has not done anything wrong, they simply need a code.
   */
  static async mine(url: string, visitorId: string | null, options: Record<string, any> = {}): Promise<OpResult> {
    return executeQueryImageSafe(
      MINE_QUERY,
      { params: { url, visitorId: visitorId || null } },
      { fetchPolicy: "network-only", ...options }
    );
  }

  /**
   * Change the details and answers on an application that has not been decided yet.
   *
   * ⚠ DETAILS AND ANSWERS ONLY (ruling 10). The package, the price, our fee and the payment are not
   * in the input at all — switching package is a refund-and-recharge, and the host's inbox must
   * never show an amount that moved under them. The server refuses the call outright once a
   * decision has been made.
   */
  static async updateMine(params: PublicPartnerUpdateMineInput): Promise<OpResult> {
    return executeMutationImageSafe(UPDATE_MINE_MUTATION, { params });
  }

  /**
   * Email a one-time code to the address on the application, so it can be reopened on another
   * browser.
   *
   * ⚠ THE RESPONSE IS THE SAME WHETHER THE ADDRESS MATCHED OR NOT — deliberately. A reply that
   * differed would let somebody type addresses at it and learn which companies have applied to an
   * event. The caller must not "helpfully" report that nothing was found, because there is nothing
   * in the response that could tell it.
   */
  static async requestCode(url: string, email: string): Promise<OpResult> {
    return ApiService.executeMutation(REQUEST_CODE_MUTATION, { params: { url, email } });
  }

  /**
   * Redeem that code on the new browser, which ADOPTS the application. Single use, 15 minutes.
   *
   * ⚠ Redeeming re-points the application at this browser rather than copying a cookie across, so
   * the previous device stops being able to edit it. One company, one live session.
   */
  static async redeemCode(url: string, code: string, visitorId: string): Promise<OpResult> {
    return executeMutationImageSafe(REDEEM_CODE_MUTATION, { params: { url, code, visitorId } });
  }
  /**
   * Forget this browser's applications for an event, so a DIFFERENT one can be restored by code.
   *
   * ⚠ NOTHING IS DELETED — the application, its answers and its money are untouched, and the host's
   * inbox does not change by a row. Only this browser's claim on it is cleared.
   *
   * ⚠ THE ONLY WAY BACK IS THE EMAILED CODE. Say so before calling this: the code is matched against
   * the address ON the application, so somebody who no longer has that inbox cannot return.
   */
  static async forgetMine(url: string, visitorId: string): Promise<OpResult> {
    return executeMutationImageSafe(FORGET_MINE_MUTATION, { params: { url, visitorId } });
  }

  /**
   * Start (or resume) the payment on an APPROVED application that still owes money.
   *
   * ⚠ It returns the SAME Stripe handoff `apply` does, so the website pays with one card component
   * and one `confirm` call whether the money was owed at application time or after approval.
   *
   * ⚠ No amount is sent. The charge is the application's FROZEN total — a host who has since raised
   * the tier cannot bill the difference to somebody already approved.
   */
  static async startPayment(applicationId: string, visitorId: string): Promise<OpResult> {
    return ApiService.executeMutation(START_PAYMENT_MUTATION, { params: { applicationId, visitorId } });
  }
  /**
   * Adopt an application from the SIGNED LINK in its own emails — no address to type, no code to
   * wait for.
   *
   * ⚠ It is the same credential as the 6-digit code, not a weaker one: whoever can read that inbox
   * could request a code and redeem it anyway. What it saves is the round trip.
   *
   * ⚠ Deliberately NOT single-use — it lives in an inbox and is the sponsor's way back, so clicking
   * it again next week must land in the same place rather than on an error.
   */
  static async redeemLink(url: string, token: string, visitorId: string): Promise<OpResult> {
    return executeMutationImageSafe(REDEEM_LINK_MUTATION, { params: { url, token, visitorId } });
  }
}
